import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import {
  apiErrorSchema,
  notificationPrefsResponseSchema,
  pushKeyResponseSchema,
  subscribePushRequestSchema,
  unsubscribePushRequestSchema,
  type NotificationPrefs,
  type NotificationType,
} from "@vmeste/shared";
import type { ZodType } from "zod";
import { t } from "../i18n/index.js";

/**
 * Web Push, from the browser's side (SPEC §4.7).
 *
 * Two things live here and nowhere else: the awkward truth about where push
 * actually works, and the four calls that turn a browser into a subscription
 * the server can reach.
 *
 * The push endpoints are being written by someone else against the shared
 * schemas, so this file talks to them with plain `fetch` and parses every reply
 * through the same zod schema the server validates with. Nothing is added to
 * `lib/api.ts` — that file belongs to the sync layer, and a half-built endpoint
 * has no business appearing in its surface.
 *
 * The service worker is likewise not ours: this hook only asks
 * `navigator.serviceWorker.ready` for the registration it already made.
 */

/* --------------------------------------------------------------- platform */

/**
 * @remarks
 * `needs-install` is deliberately checked before `unsupported`. On iOS, Safari
 * in a tab simply does not expose `PushManager` — so the generic "your browser
 * cannot do this" branch would swallow the one case that has a fix, and tell
 * somebody their iPhone is incapable when it is one menu away.
 */
export type PushStatus =
  | "checking"
  | "unsupported"
  | "needs-install"
  | "denied"
  | "idle"
  | "subscribed";

interface Environment {
  /** iPhone, iPad or iPod — including an iPad pretending to be a Mac. */
  ios: boolean;
  /**
   * Safari rather than Chrome/Firefox/Edge for iOS. All of them are WebKit
   * underneath, but only Safari can put an app on the home screen, and only a
   * home-screen app gets push.
   */
  iosSafari: boolean;
  /** Installed: home screen on iOS, Dock on macOS, WebAPK on Android. */
  standalone: boolean;
  /** Every API a subscription needs is present. */
  capable: boolean;
}

function readEnvironment(): Environment {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { ios: false, iosSafari: false, standalone: false, capable: false };
  }

  const ua = navigator.userAgent;
  const ios =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as "Macintosh". A Mac has no touch screen, so
    // touch points on a Mac user agent means iPad.
    (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);

  // Chrome, Firefox, Edge and Opera for iOS all append their own token.
  const iosSafari = ios && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(ua);

  const nav = navigator as Navigator & { standalone?: boolean };
  const standalone =
    // Safari's own flag, the only reliable answer on older iOS.
    nav.standalone === true ||
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches);

  const capable =
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  return { ios, iosSafari, standalone, capable };
}

/** The part of the answer that needs no async work. Null means "look closer". */
function statusFromEnvironment(env: Environment): PushStatus | null {
  if (env.ios && !env.standalone) return "needs-install";
  if (!env.capable) return "unsupported";
  // Asking again does nothing once it is denied — the browser will not even
  // show the sheet — so this is a dead end until the person changes it in
  // settings, and the UI has to say so rather than offer a button.
  if (Notification.permission === "denied") return "denied";
  return null;
}

/* ------------------------------------------------------------------ vapid */

/**
 * The VAPID public key travels as base64url text; `pushManager.subscribe` wants
 * raw bytes. `atob` only speaks standard base64, hence the two substitutions
 * and the padding.
 */
// The return type is inferred rather than written: `Uint8Array` on its own now
// means `Uint8Array<ArrayBufferLike>`, which includes SharedArrayBuffer and so
// is not a `BufferSource`. Inference keeps the narrow `Uint8Array<ArrayBuffer>`
// that `pushManager.subscribe` actually wants.
export function urlBase64ToBytes(base64Url: string) {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/* ------------------------------------------------------------------ fetch */

const PUSH_BASE = "/api/push";

/** Anything, for the 204s where there is no body worth validating. */
const noBody: ZodType<unknown> = z.unknown();

class PushError extends Error {
  override name = "PushError";
}

async function pushRequest<T>(
  path: string,
  schema: ZodType<T>,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${PUSH_BASE}${path}`, {
      method: init.method ?? "GET",
      // The session is an httpOnly cookie (SPEC §8) — nothing here holds a token.
      credentials: "same-origin",
      ...(init.body === undefined
        ? {}
        : {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(init.body),
          }),
      ...(init.signal ? { signal: init.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new PushError(t("settings.pushNoConnection"));
  }

  const text = await response.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : undefined;
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    if (response.status === 404) {
      // Honest about the actual state of the world: the endpoints are still
      // being written. Better than "что-то пошло не так".
      throw new PushError(t("settings.pushNotReady"));
    }
    const parsed = apiErrorSchema.safeParse(payload);
    throw new PushError(
      parsed.success
        ? parsed.data.error.message
        : t("settings.pushServerError", { status: response.status }),
    );
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) throw new PushError(t("settings.pushBadResponse"));
  return parsed.data;
}

function messageFor(cause: unknown): string {
  if (cause instanceof PushError) return cause.message;
  if (cause instanceof DOMException) {
    if (cause.name === "NotAllowedError") return t("settings.pushRefused");
    if (cause.name === "AbortError") return t("settings.pushAborted");
  }
  return t("settings.pushFailed");
}

/* ------------------------------------------------------------------ state */

export type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

export interface NotificationsController {
  status: PushStatus;
  /** Meaningful only while `status` is `needs-install`. */
  iosSafari: boolean;
  /** A subscribe or unsubscribe is in flight. */
  busy: boolean;
  /** Something went wrong with the subscription itself. */
  error: string | null;

  prefs: NotificationPrefs | null;
  prefsLoading: boolean;
  prefsError: string | null;
  save: SaveState;

  /** What this device thinks the timezone is, right now. */
  deviceTimeZone: string;

  enable(): Promise<void>;
  disable(): Promise<void>;
  setTypeEnabled(type: NotificationType, on: boolean): void;
  setQuietHours(from: string | null, to: string | null): void;
  setLeadMinutes(field: LeadField, minutes: number): void;
  retrySave(): void;
  reloadPrefs(): void;
}

/**
 * The two preferences that are a number of minutes of notice.
 *
 * Named as a union of the schema's own keys rather than as a second enum, so
 * renaming a field in `@vmeste/shared` breaks here rather than writing an
 * unknown key into the patch and being answered with a 400.
 */
export type LeadField = "eventLeadMinutes" | "itemLeadMinutes";

/** Long enough that dragging a time field does not fire a request per minute. */
const SAVE_DELAY_MS = 700;
/** How long "Сохранено" stays on screen. */
const SAVED_LINGER_MS = 2500;
/** A registration that never arrives should not spin forever. */
const SW_READY_TIMEOUT_MS = 8000;

async function registrationOrNull(): Promise<ServiceWorkerRegistration | null> {
  // `serviceWorker.ready` never rejects and never resolves when nothing is
  // registered, which would leave the panel saying "проверяем" for good.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS)),
  ]);
}

export function useNotifications(): NotificationsController {
  const [env, setEnv] = useState<Environment>(() => readEnvironment());
  const [status, setStatus] = useState<PushStatus>("checking");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  /**
   * The queue function, reachable from the effect that loads preferences.
   *
   * That effect is declared above `queue`, and naming it in the dependency
   * array would read it during render — before it exists. A ref is the smaller
   * of the two evils; the alternative is moving a hundred lines around to
   * satisfy declaration order.
   */
  const queueRef = useRef<((patch: Partial<NotificationPrefs>) => void) | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const [prefsNonce, setPrefsNonce] = useState(0);
  const [save, setSave] = useState<SaveState>({ kind: "idle" });

  const deviceTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  /* ------------------------------------------------------------- status */

  const refresh = useCallback(async () => {
    const next = readEnvironment();
    setEnv(next);

    const immediate = statusFromEnvironment(next);
    if (immediate) {
      setStatus(immediate);
      return;
    }

    const registration = await registrationOrNull();
    if (!registration) {
      setStatus("idle");
      setError(t("settings.pushSwNotReadyReload"));
      return;
    }

    try {
      const subscription = await registration.pushManager.getSubscription();
      setStatus(subscription ? "subscribed" : "idle");
    } catch {
      setStatus("idle");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Permission and installed-ness both change outside this page: somebody grants
  // notifications in browser settings, or adds the app to the home screen and
  // reopens it. Re-checking when the tab comes back is the cheapest way to stop
  // showing a stale dead end.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  /* -------------------------------------------------------- subscription */

  const enable = useCallback(async () => {
    setError(null);
    setBusy(true);

    // Tracked so a failure after this point can undo it: a browser subscription
    // the server never heard about is worse than none — the UI would say
    // "включены" about pushes nobody will ever send.
    let created: PushSubscription | null = null;

    try {
      const current = readEnvironment();
      setEnv(current);
      if (current.ios && !current.standalone) {
        setStatus("needs-install");
        return;
      }
      if (!current.capable) {
        setStatus("unsupported");
        return;
      }

      // Nothing is awaited before this line on purpose. The call is only allowed
      // while the browser still counts the click as user activation, and an
      // await in between spends it.
      const permission = await Notification.requestPermission();
      if (permission === "denied") {
        setStatus("denied");
        return;
      }
      if (permission !== "granted") {
        setStatus("idle");
        setError(t("settings.pushNoPermission"));
        return;
      }

      const registration = await registrationOrNull();
      if (!registration) throw new PushError(t("settings.pushSwNotReady"));

      const { publicKey } = await pushRequest("/key", pushKeyResponseSchema);

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          // Required by every browser, and on iOS a push that shows no
          // notification is a step towards losing the permission (SPEC §3).
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToBytes(publicKey),
        });
        created = subscription;
      }

      const json = subscription.toJSON();
      let body;
      try {
        body = subscribePushRequestSchema.parse({
          subscription: {
            endpoint: json.endpoint,
            keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
          },
        });
      } catch {
        throw new PushError(t("settings.pushBadSubscription"));
      }

      await pushRequest("/subscribe", noBody, { method: "POST", body });
      created = null;
      setStatus("subscribed");
    } catch (cause) {
      if (created) {
        // Roll back rather than leave a subscription only the browser knows about.
        await created.unsubscribe().catch(() => undefined);
      }
      setError(messageFor(cause));
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const registration = await registrationOrNull();
      const subscription = registration
        ? await registration.pushManager.getSubscription()
        : null;

      if (!subscription) {
        setStatus("idle");
        return;
      }

      // The server forgets first. Dropping the browser's subscription before it
      // does would leave the sender pushing at an endpoint that is gone, and
      // nothing on this device could ever tell it to stop.
      await pushRequest("/unsubscribe", noBody, {
        method: "POST",
        body: unsubscribePushRequestSchema.parse({ endpoint: subscription.endpoint }),
      });
      await subscription.unsubscribe();
      setStatus("idle");
    } catch (cause) {
      setError(messageFor(cause));
    } finally {
      setBusy(false);
    }
  }, []);

  /* --------------------------------------------------------------- prefs */

  useEffect(() => {
    const controller = new AbortController();
    setPrefsLoading(true);
    setPrefsError(null);

    pushRequest("/prefs", notificationPrefsResponseSchema, {
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        setPrefs(response.prefs);

        /*
         * The device decides the time zone, always.
         *
         * "Не беспокоить с 23:00" means 23:00 where the person is now. A stored
         * zone that has drifted — a trip, a phone set up somewhere else, a
         * default of UTC nobody ever changed — makes quiet hours land at the
         * wrong hour, and the person has no way of telling from the screen.
         *
         * So it is reconciled here rather than offered as a choice: there was a
         * button asking whether to adopt this device's zone, and the honest
         * answer to that question is always yes.
         */
        if (response.prefs.timeZone !== deviceTimeZone) {
          queueRef.current?.({ timeZone: deviceTimeZone });
        }
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        // Not swallowed and not replaced with a plausible-looking default:
        // showing every switch "on" when we never read them would be a lie the
        // person acts on.
        setPrefsError(
          cause instanceof PushError ? cause.message : t("settings.prefsLoadFailed"),
        );
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setPrefsLoading(false);
      });

    return () => controller.abort();
  }, [prefsNonce]);

  /* ---------------------------------------------------------- saving */

  const queued = useRef<Partial<NotificationPrefs>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(async () => {
    const patch = queued.current;
    queued.current = {};
    if (Object.keys(patch).length === 0) return;

    setSave({ kind: "saving" });
    try {
      const response = await pushRequest("/prefs", notificationPrefsResponseSchema, {
        method: "PATCH",
        body: patch,
      });
      // Anything toggled while the request was in flight is still queued, and
      // must survive the server's answer — otherwise the switch the person just
      // flipped snaps back under their finger.
      setPrefs({ ...response.prefs, ...queued.current });
      setSave({ kind: "saved" });
    } catch (cause) {
      // The patch goes back in the queue so "Повторить" resends exactly what
      // never landed, and the local value stays where the person put it.
      queued.current = { ...patch, ...queued.current };
      setSave({
        kind: "error",
        message: cause instanceof PushError ? cause.message : t("settings.saveFailed"),
      });
    }
  }, []);

  const queue = useCallback(
    (patch: Partial<NotificationPrefs>) => {
      setPrefs((prev) => (prev ? { ...prev, ...patch } : prev));
      queued.current = { ...queued.current, ...patch };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), SAVE_DELAY_MS);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  useEffect(() => {
    if (save.kind !== "saved") return;
    const t = setTimeout(() => setSave({ kind: "idle" }), SAVED_LINGER_MS);
    return () => clearTimeout(t);
  }, [save]);

  const setTypeEnabled = useCallback(
    (type: NotificationType, on: boolean) => {
      if (!prefs) return;
      const next = on
        ? prefs.enabled.includes(type)
          ? prefs.enabled
          : [...prefs.enabled, type]
        : prefs.enabled.filter((value) => value !== type);
      queue({ enabled: next });
    },
    [prefs, queue],
  );

  const setQuietHours = useCallback(
    (from: string | null, to: string | null) => {
      // The timezone rides along with every quiet-hours change: "не беспокоить с
      // 23:00" has to mean 23:00 where the person is, not where they were when
      // they set it.
      queue({ quietFrom: from, quietTo: to, timeZone: deviceTimeZone });
    },
    [queue, deviceTimeZone],
  );

  // Same debounced queue as everything else here: a person trying the presets
  // one after another should cost one request, not five.
  const setLeadMinutes = useCallback(
    (field: LeadField, minutes: number) => {
      queue({ [field]: minutes });
    },
    [queue],
  );

  queueRef.current = queue;

  const retrySave = useCallback(() => {
    void flush();
  }, [flush]);

  const reloadPrefs = useCallback(() => {
    setPrefsNonce((n) => n + 1);
  }, []);

  return {
    status,
    iosSafari: env.iosSafari,
    busy,
    error,
    prefs,
    prefsLoading,
    prefsError,
    save,
    deviceTimeZone,
    enable,
    disable,
    setTypeEnabled,
    setQuietHours,
    setLeadMinutes,
    retrySave,
    reloadPrefs,
  };
}
